/** A run of assistant Markdown, or a pipe table found in it. */
export type AssistantBlock =
  | { type: 'text'; text: string }
  | { type: 'table'; header: string[]; rows: string[][] }

const SEPARATOR = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/
const FENCE = /^\s*(```|~~~)/

const isRow = (line: string) => line.includes('|')
const splitRow = (line: string) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim())

/**
 * Split assistant text into Markdown runs and pipe tables. While it streams (`done` false), a row
 * counts only once a newline follows it, and a header waits for its separator: a half-typed row
 * never shows as raw pipes. Pipes inside code fences stay text.
 */
export function parseAssistantBlocks(text: string, done = true): AssistantBlock[] {
  const lines = text.split('\n')
  const last = lines.length - 1
  const complete = (i: number) => done || i < last
  const blocks: AssistantBlock[] = []
  let buf: string[] = []
  let inFence = false

  const flush = () => {
    while (buf.length > 0 && !buf[buf.length - 1].trim()) buf.pop()
    if (buf.length > 0) blocks.push({ type: 'text', text: buf.join('\n') })
    buf = []
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (FENCE.test(line)) inFence = !inFence
    if (!inFence && isRow(line) && i + 1 < lines.length && complete(i + 1) && SEPARATOR.test(lines[i + 1])) {
      const header = splitRow(line)
      const rows: string[][] = []
      let j = i + 2
      while (j < lines.length && isRow(lines[j]) && lines[j].trim()) {
        if (complete(j)) rows.push(splitRow(lines[j]))
        j++
      }
      flush()
      blocks.push({ type: 'table', header, rows })
      i = j - 1
      while (i + 1 < lines.length && !lines[i + 1].trim()) i++
      continue
    }
    // A pipe line still waiting for its separator, or half typed, is held back while streaming.
    if (!done && !inFence && isRow(line) && i >= last - 1) break
    buf.push(line)
  }
  flush()
  return blocks
}

export const hasTable = (blocks: AssistantBlock[]) => blocks.some((b) => b.type === 'table')
